import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CaretDown, List, X, Phone } from "@phosphor-icons/react";
import { LOGO_URL, CONTACT } from "../data/content";

const MORTGAGE_LINKS = [
  { to: "/mortgages/first-time-buyer", label: "First-Time Buyer" },
  { to: "/mortgages/next-time-buyer", label: "Next-Time Buyer" },
  { to: "/mortgages/remortgage", label: "Remortgage" },
  { to: "/mortgages/buy-to-let", label: "Buy-to-Let" },
];

const PROTECTION_LINKS = [
  { to: "/mortgages/protection", label: "Protection" },
  { to: "/mortgages/whole-of-life", label: "Whole of Life" },
  { to: "/mortgages/business-protection", label: "Business Protection" },
];

const RESOURCE_LINKS = [
  { to: "/calculators", label: "Calculators" },
  { to: "/calculators?tab=stamp-duty", label: "Stamp Duty Calculator" },
  { to: "/resources/jargon-buster", label: "Jargon Buster" },
  { to: "/resources/guides", label: "Guides" },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const linkClass = ({ isActive }) =>
  `text-[0.72rem] font-semibold uppercase tracking-[0.22em] transition-colors duration-200 hover:text-brand ${
    isActive ? "text-brand" : "text-neutral-700"
  }`;

const Dropdown = ({ label, links, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={testId}
        className="flex items-center gap-1.5 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-neutral-700 transition-colors duration-200 hover:text-brand"
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 top-full -translate-x-1/2 pt-4"
          >
            <div className="w-72 border border-black/10 bg-white p-2 shadow-[0_24px_60px_rgba(10,19,38,0.12)]">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${slugify(l.label)}`}
                  className="block px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-neutral-700 transition-colors duration-150 hover:bg-brand-bone hover:text-brand"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.05)]"
          : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[76px] items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="BTK Mortgage Services" className="h-11 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
            <NavLink to="/" end data-testid="nav-home" className={linkClass}>
              Home
            </NavLink>
            <NavLink to="/about" data-testid="nav-about" className={linkClass}>
              About
            </NavLink>
            <Dropdown label="Mortgages" links={MORTGAGE_LINKS} testId="nav-mortgages-dropdown" />
            <Dropdown label="Protection" links={PROTECTION_LINKS} testId="nav-protection-dropdown" />
            <NavLink to="/how-it-works" data-testid="nav-how-it-works" className={linkClass}>
              How It Works
            </NavLink>
            <Dropdown label="Resources" links={RESOURCE_LINKS} testId="nav-resources-dropdown" />
            <NavLink to="/resources/refer-a-friend" data-testid="nav-refer-a-friend" className={linkClass}>
              Refer a Friend
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-link"
              className="hidden xl:flex items-center gap-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:text-brand"
            >
              <Phone size={17} weight="duotone" className="text-brand" />
              {CONTACT.phone}
            </a>
            <button
              data-testid="header-book-call-btn"
              onClick={() => navigate("/contact")}
              className="hidden sm:inline-flex items-center bg-brand-ink px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-200 hover:bg-brand hover:-translate-y-0.5"
            >
              Book a call
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 text-neutral-800"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-black/5 bg-white/95 backdrop-blur-xl"
            aria-label="Mobile"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              <Link to="/" data-testid="mobile-nav-home" className="rounded-xl px-3 py-3 text-sm font-semibold text-neutral-800 hover:bg-secondary">
                Home
              </Link>
              <Link to="/about" data-testid="mobile-nav-about" className="rounded-xl px-3 py-3 text-sm font-semibold text-neutral-800 hover:bg-secondary">
                About
              </Link>
              <p className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Mortgages</p>
              {MORTGAGE_LINKS.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${slugify(l.label)}`} className="rounded-xl px-3 py-3 text-sm font-medium text-neutral-700 hover:bg-secondary">
                  {l.label}
                </Link>
              ))}
              <p className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Protection</p>
              {PROTECTION_LINKS.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${slugify(l.label)}`} className="rounded-xl px-3 py-3 text-sm font-medium text-neutral-700 hover:bg-secondary">
                  {l.label}
                </Link>
              ))}
              <p className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Resources</p>
              {RESOURCE_LINKS.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${slugify(l.label)}`} className="rounded-xl px-3 py-3 text-sm font-medium text-neutral-700 hover:bg-secondary">
                  {l.label}
                </Link>
              ))}
              <Link to="/how-it-works" data-testid="mobile-nav-how-it-works" className="rounded-xl px-3 py-3 text-sm font-semibold text-neutral-800 hover:bg-secondary">
                How It Works
              </Link>
              <Link to="/resources/refer-a-friend" data-testid="mobile-nav-refer-a-friend" className="rounded-xl px-3 py-3 text-sm font-semibold text-neutral-800 hover:bg-secondary">
                Refer a Friend
              </Link>
              <Link
                to="/contact"
                data-testid="mobile-nav-contact"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white"
              >
                Book a call
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
